import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { FlowProject, NodeData, EdgeData, BehaviorTree } from 'src/types/flow';
import { API_PREFIX } from 'src/config/api';

interface FlowState {
    currentProject: FlowProject | null;
    projects: FlowProject[];
    behaviorTrees: BehaviorTree[];
    selectedNodes: string[];
    clipboard: {
        nodes: NodeData[];
        edges: EdgeData[];
    } | null;
}

const initialState: FlowState = {
    currentProject: null,
    projects: [],
    behaviorTrees: [],
    selectedNodes: [],
    clipboard: null,
};

// Async thunks to persist flows to backend

export const fetchFlows = createAsyncThunk('flow/fetchFlows', async () => {
    const res = await fetch(`${API_PREFIX}/api/flows`);
    if (!res.ok) throw new Error('Failed to fetch flows');
    const data = await res.json();
    // deserialize dates
    return data.map((p: any) => ({ ...p, created_at: new Date(p.created_at), updated_at: new Date(p.updated_at) }));
});

export const saveFlow = createAsyncThunk('flow/saveFlow', async (project: FlowProject) => {
    const method = project.id ? 'PUT' : 'POST';
    const url = project.id ? `${API_PREFIX}/api/flows/${project.id}` : `${API_PREFIX}/api/flows`;
    const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(project)
    });
    if (!res.ok) throw new Error('Failed to save flow');
    const saved = await res.json();
    return { ...saved, created_at: new Date(saved.created_at), updated_at: new Date(saved.updated_at) };
});

export const deleteFlow = createAsyncThunk('flow/deleteFlow', async (id: string) => {
    const res = await fetch(`${API_PREFIX}/api/flows/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete flow');
    return id;
});

const flowSlice = createSlice({
    name: 'flow',
    initialState,
    reducers: {
        createProject: (state, action: PayloadAction<Omit<FlowProject, 'id' | 'created_at' | 'updated_at'>>) => {
            const project: FlowProject = {
                ...action.payload,
                id: crypto.randomUUID(),
                created_at: new Date(),
                updated_at: new Date(),
            };
            state.projects.push(project);
            state.currentProject = project;
        },
        loadProject: (state, action: PayloadAction<string>) => {
            const project = state.projects.find(p => p.id === action.payload);
            if (project) {
                state.currentProject = project;
            }
        },
        updateProject: (state, action: PayloadAction<Partial<FlowProject>>) => {
            if (state.currentProject) {
                state.currentProject = {
                    ...state.currentProject,
                    ...action.payload,
                    updated_at: new Date(),
                };
                const index = state.projects.findIndex(p => p.id === state.currentProject!.id);
                if (index !== -1) {
                    state.projects[index] = state.currentProject;
                }
            }
        },
        addNode: (state, action: PayloadAction<NodeData>) => {
            if (state.currentProject) {
                state.currentProject.nodes.push(action.payload);
                state.currentProject.updated_at = new Date();
            }
        },
        updateNode: (state, action: PayloadAction<NodeData>) => {
            if (state.currentProject) {
                const index = state.currentProject.nodes.findIndex(n => n.id === action.payload.id);
                if (index !== -1) {
                    state.currentProject.nodes[index] = action.payload;
                    state.currentProject.updated_at = new Date();
                }
            }
        },
        removeNode: (state, action: PayloadAction<string>) => {
            if (state.currentProject) {
                state.currentProject.nodes = state.currentProject.nodes.filter(n => n.id !== action.payload);
                state.currentProject.edges = state.currentProject.edges.filter(
                    e => e.source !== action.payload && e.target !== action.payload
                );
                state.currentProject.updated_at = new Date();
            }
        },
        addEdge: (state, action: PayloadAction<EdgeData>) => {
            if (state.currentProject) {
                state.currentProject.edges.push(action.payload);
                state.currentProject.updated_at = new Date();
            }
        },
        updateEdge: (state, action: PayloadAction<EdgeData>) => {
            if (state.currentProject) {
                const index = state.currentProject.edges.findIndex(e => e.id === action.payload.id);
                if (index !== -1) {
                    state.currentProject.edges[index] = action.payload;
                    state.currentProject.updated_at = new Date();
                }
            }
        },
        removeEdge: (state, action: PayloadAction<string>) => {
            if (state.currentProject) {
                state.currentProject.edges = state.currentProject.edges.filter(e => e.id !== action.payload);
                state.currentProject.updated_at = new Date();
            }
        },
        setSelectedNodes: (state, action: PayloadAction<string[]>) => {
            state.selectedNodes = action.payload;
        },
        copyToClipboard: (state, action: PayloadAction<{ nodes: NodeData[]; edges: EdgeData[] }>) => {
            state.clipboard = action.payload;
        },
        createBehaviorTree: (state, action: PayloadAction<Omit<BehaviorTree, 'id'>>) => {
            const behaviorTree: BehaviorTree = {
                ...action.payload,
                id: crypto.randomUUID(),
            };
            state.behaviorTrees.push(behaviorTree);
        },
        updateBehaviorTree: (state, action: PayloadAction<BehaviorTree>) => {
            const index = state.behaviorTrees.findIndex(bt => bt.id === action.payload.id);
            if (index !== -1) {
                state.behaviorTrees[index] = action.payload;
            }
        },
        deleteBehaviorTree: (state, action: PayloadAction<string>) => {
            state.behaviorTrees = state.behaviorTrees.filter(bt => bt.id !== action.payload);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchFlows.fulfilled, (state, action: PayloadAction<FlowProject[]>) => {
            state.projects = action.payload;
        });
        builder.addCase(saveFlow.fulfilled, (state, action: PayloadAction<FlowProject>) => {
            const existing = state.projects.findIndex(p => p.id === action.payload.id);
            if (existing !== -1) {
                state.projects[existing] = action.payload;
            } else {
                state.projects.push(action.payload);
            }
            state.currentProject = action.payload;
        });
        builder.addCase(deleteFlow.fulfilled, (state, action: PayloadAction<string>) => {
            state.projects = state.projects.filter(p => p.id !== action.payload);
            if (state.currentProject?.id === action.payload) {
                state.currentProject = null;
            }
        });
    }
});

export const {
    createProject,
    loadProject,
    updateProject,
    addNode,
    updateNode,
    removeNode,
    addEdge,
    updateEdge,
    removeEdge,
    setSelectedNodes,
    copyToClipboard,
    createBehaviorTree,
    updateBehaviorTree,
    deleteBehaviorTree,
} = flowSlice.actions;

export default flowSlice.reducer;